import React, { useEffect, useState } from 'react';
import { Card, Table, Button, ButtonGroup, Row, Col } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import API from '../services/api';
import { bareDate, monthLabel } from '../utils/dates';
import Loading from './Loading';
import { billingNote } from '../utils/rentalBilling';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const PERIOD_LABEL = { daily: 'Diario', weekly: 'Semanal', monthly: 'Mensual' };
const HORIZONS = [[3, '3 meses'], [6, '6 meses'], [12, '12 meses']];

// "Proyección" (admins): rental income the current rentals will bill month by month, from GET /rentals/projection.
// Amounts before ITBIS are the income; ITBIS is collected for DGII. Assumes every active rental keeps running until its
// end date (open-ended ones until the horizon) and nothing new is rented — a floor, not a forecast of new business.
const RentalProjectionTab = () => {
  const [months, setMonths] = useState(6);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    setData(null);
    API.get('/rentals/projection', { params: { months } })
      .then(res => setData(res.data))
      .catch(err => setError(err.response?.data?.message || 'Error al cargar la proyección.'));
  }, [months]);

  if (!data) return <Card className="shadow-sm p-3">{error ? <div className="alert alert-danger mb-0">{error}</div> : <Loading label="Cargando proyección..." />}</Card>;

  const chart = {
    labels: data.months.map(m => monthLabel(m.month, true)),
    datasets: [
      { label: 'Ingreso (sin ITBIS)', data: data.months.map(m => m.subtotal), backgroundColor: 'rgba(45,212,191,0.65)', borderRadius: 4, stack: 's' },
      { label: 'ITBIS', data: data.months.map(m => m.itbis), backgroundColor: 'rgba(157,179,194,0.45)', borderRadius: 4, stack: 's' },
    ],
  };
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } } },
    scales: { x: { stacked: true }, y: { stacked: true, ticks: { callback: (v) => money(v).replace(/\.00$/, '') } } },
  };

  const noRentals = data.rentals.active + data.rentals.awaitingFirstInvoice === 0;

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div className="text-muted small" style={{ maxWidth: 760 }}>
          Lo que facturarán los alquileres activos (y los que tienen contrato pero aún no su primera factura), suponiendo que
          siguen hasta su fecha de fin — los indefinidos, hasta el final del período — y sin alquileres nuevos. Las facturas
          vencidas sin facturar cuentan en el mes actual.
        </div>
        <ButtonGroup size="sm">
          {HORIZONS.map(([n, label]) => (
            <Button key={n} variant={months === n ? 'primary' : 'outline-secondary'} onClick={() => setMonths(n)}>{label}</Button>
          ))}
        </ButtonGroup>
      </div>

      <Row className="mb-3">
        <Col md={6} xl={3} className="mb-3 mb-xl-0">
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Ingreso recurrente mensual</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(data.monthlyRunRate)}</h2>
            <div className="small text-muted mt-1">sin ITBIS · alquileres en curso, llevados a un mes</div>
          </Card>
        </Col>
        <Col md={6} xl={3} className="mb-3 mb-xl-0">
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Proyectado — {monthLabel(data.months[0].month)}</div>
            <h2 className="mt-2 mb-0">{money(data.months[0].subtotal)}</h2>
            <div className="small text-muted mt-1">{data.months[0].invoices} {data.months[0].invoices === 1 ? 'factura' : 'facturas'} · {money(data.months[0].total)} con ITBIS</div>
          </Card>
        </Col>
        <Col md={6} xl={3} className="mb-3 mb-md-0">
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Proyectado — próximos {months} meses</div>
            <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-green)' }}>{money(data.totals.subtotal)}</h2>
            <div className="small text-muted mt-1">{data.totals.invoices} facturas · {money(data.totals.total)} con ITBIS</div>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="shadow-sm p-3 dashboard-card">
            <div className="text-muted small">Alquileres que facturan</div>
            <h2 className="mt-2 mb-0">{data.rentals.active}</h2>
            <div className="small text-muted mt-1">activos{data.rentals.awaitingFirstInvoice ? ` · ${data.rentals.awaitingFirstInvoice} por su primera factura` : ''}</div>
          </Card>
        </Col>
      </Row>

      {noRentals ? (
        <Card className="shadow-sm p-4 text-center text-muted">No hay alquileres activos para proyectar.</Card>
      ) : (
        <>
          <Row className="mb-3">
            <Col xl={8} className="mb-3 mb-xl-0">
              <Card className="shadow-sm p-3 card-equal-height">
                <Card.Title>Ingreso proyectado por mes</Card.Title>
                <div style={{ height: 300 }}><Bar data={chart} options={chartOptions} /></div>
              </Card>
            </Col>
            <Col xl={4}>
              <Card className="shadow-sm p-3 card-equal-height">
                <Card.Title>Por categoría ({months} meses, sin ITBIS)</Card.Title>
                <Table size="sm" className="mb-0">
                  <tbody>
                    {data.byCategory.map(c => (
                      <tr key={c.category}>
                        <td>{c.category}</td>
                        <td className="text-end">{money(c.subtotal)}</td>
                        <td className="text-end text-muted small" style={{ width: 60 }}>
                          {data.totals.subtotal ? `${((c.subtotal / data.totals.subtotal) * 100).toFixed(0)}%` : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm p-3">
            <Card.Title>Por alquiler (sin ITBIS)</Card.Title>
            <Table hover responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Alquiler</th><th>Cliente</th><th>Período</th><th title="Cuándo toca hacer la próxima factura del alquiler">Facturar</th><th className="text-end">Por ciclo</th>
                  {data.months.map(m => <th key={m.month} className="text-end">{monthLabel(m.month, true)}</th>)}
                  <th className="text-end">Total</th>
                </tr>
              </thead>
              <tbody>
                {data.perRental.map(r => (
                  <tr key={r.rentalId}>
                    <td>{r.rentalNumber}{r.overdue > 0 && <div className="small" style={{ color: 'var(--mm-red)' }}>{r.overdue} vencida{r.overdue === 1 ? '' : 's'}</div>}</td>
                    <td>{r.client}</td>
                    <td>{PERIOD_LABEL[r.billingPeriod] || r.billingPeriod}</td>
                    <td className="text-nowrap">
                      {r.nextDate ? bareDate(r.nextDate) : '—'}
                      {billingNote(r.nextDate) && <div className="small" style={{ color: billingNote(r.nextDate).color }}>{billingNote(r.nextDate).text}</div>}
                    </td>
                    <td className="text-end">{money(r.perCycle)}</td>
                    {r.byMonth.map((v, i) => <td key={i} className="text-end" style={{ color: v ? undefined : 'var(--mm-muted)' }}>{v ? money(v) : '—'}</td>)}
                    <td className="text-end" style={{ fontWeight: 600 }}>{money(r.subtotal)}</td>
                  </tr>
                ))}
                <tr style={{ borderTop: '2px solid var(--mm-line)', fontWeight: 600 }}>
                  <td colSpan={5}>Total</td>
                  {data.months.map(m => <td key={m.month} className="text-end">{money(m.subtotal)}</td>)}
                  <td className="text-end">{money(data.totals.subtotal)}</td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </>
  );
};

export default RentalProjectionTab;
